import { useAccount, useReadContract, useReadContracts } from "wagmi";
import useDappConfig from "./useDappConfig";
import { Hash } from "viem";
import { ETH, TokenDetails } from "@config/contractsConfig";

const withdrawalDelays = {
  LST: {
    final: 7200n * 22n,
    initial: 7200n * 16n,
  },
  ETH: {
    final: 7200n * 22n,
    initial: 7200n * 16n,
  },
};

const useWithdrawals = () => {
  const { address } = useAccount();
  //   const { isEthereumChain, unsupported } = useDappChain();
  const { config, isWithdrawSupported } = useDappConfig();

  const tokensObj = {
    ...config.tokens,
    [ETH]: config.nativeToken as any,
  } as TokenDetails;
  const tokens = Object.keys(tokensObj).map((token) => tokensObj[token]);

  const { data: withdrawalDelayBlocks } = useReadContract({
    ...config.interactionPools.lrtWithdrawalManager,
    functionName: "withdrawalDelayBlocks",
    query: {
      enabled: !!address && !!tokens.length && isWithdrawSupported,
    },
  });

  const {
    data: tokensAssociatedNonces,
    refetch: refetchAssociatedNonces,
    isFetching: fetchingAssociatedNonces,
  } = useReadContracts({
    contracts: tokens.map((token) => ({
      ...config.interactionPools.lrtWithdrawalManager,
      functionName: "userAssociatedNonces",
      args: [token.address, address],
    })),
    query: {
      enabled: !!address && !!tokens.length && isWithdrawSupported,
      refetchInterval: 10_000,
    },
  });

  const {
    data: nextLockedNonces,
    refetch: refetchNextLockedNonces,
    isFetching: fetchingNextLockedNonces,
  } = useReadContracts({
    contracts: tokens.map((token) => ({
      ...config.interactionPools.lrtWithdrawalManager,
      functionName: "nextLockedNonce",
      args: [token.address],
    })),
    query: {
      enabled: !!tokens.length && isWithdrawSupported,
      refetchInterval: 10_000,
    },
  });

  const nextLockedNoncesByAssests: { [x: string]: bigint } = nextLockedNonces
    ? tokens.reduce(
        (acc, curr, index) => ({
          ...acc,
          [curr.address.toLowerCase()]: nextLockedNonces?.[index].result,
        }),
        {}
      )
    : {};

  const associatedNoncesHasError =
    tokensAssociatedNonces?.some((req) => req.error) || false;

  const associatedNoncesPerAsset: { [x: string]: number[] } = tokens.reduce(
    (acc, curr, index) => ({
      ...acc,
      [curr.symbol]:
        tokensAssociatedNonces && !associatedNoncesHasError
          ? {
              associatedNonces: (
                tokensAssociatedNonces[index].result as bigint[]
              )?.map((val: bigint) => Number(val)) || [0, 0],
              count: Number(
                (tokensAssociatedNonces[index].result as bigint[])[1] -
                  (tokensAssociatedNonces[index].result as bigint[])[0]
              ),
            }
          : { associatedNonces: [0, 0], count: 0 },
    }),
    {}
  );

  const tokensWithAssociatedNonces = Object.keys(associatedNoncesPerAsset).map(
    (key) => ({
      ...tokensObj[key],
      ...associatedNoncesPerAsset[key],
    })
  );

  const filteredTokenWithWithdrawals = tokensWithAssociatedNonces.filter(
    (token: any) => token.count
  );

  const contractCallsToFetchWithdrawals = filteredTokenWithWithdrawals
    .map((token: any) => {
      let calls = [];
      const end = token.associatedNonces[1] - token.associatedNonces[0];

      for (let i = 0; i < end; i++) {
        calls.push({
          ...config.interactionPools.lrtWithdrawalManager,
          functionName: "getUserWithdrawalRequest",
          args: [token.address, address, i],
          symbol: token.symbol,
          index: i,
        });
      }

      return calls;
    })
    .flat();

  const { data: userWithdrawalRequests, isFetching: fetchingRequests } =
    useReadContracts({
      contracts: contractCallsToFetchWithdrawals,
      query: {
        enabled:
          !!address &&
          !!filteredTokenWithWithdrawals.length &&
          isWithdrawSupported,
      },
    });

  const withdrawalRequestsHasError =
    userWithdrawalRequests?.some((req) => req.error) || false;

  const userWithdrawals =
    userWithdrawalRequests &&
    withdrawalDelayBlocks &&
    nextLockedNonces &&
    !withdrawalRequestsHasError
      ? contractCallsToFetchWithdrawals.map((call, index) => ({
          asset: tokensObj[call.symbol].address,
          symbol: call.symbol,
          index: call.index,
          rsETHAmount: (
            userWithdrawalRequests[index].result as bigint[]
          )?.[0] as bigint,
          nonce: (
            userWithdrawalRequests[index].result as bigint[]
          )?.[3] as bigint,
          nextLockedNonce:
            nextLockedNoncesByAssests[
              tokensObj[call.symbol].address.toLowerCase()
            ],
          isUnlocked:
            ((
              userWithdrawalRequests[index].result as bigint[]
            )?.[3] as bigint) <
            nextLockedNoncesByAssests[
              tokensObj[call.symbol].address.toLowerCase()
            ],
          expectedAssetAmount: (
            userWithdrawalRequests[index].result as bigint[]
          )?.[1] as bigint,
          startTime: Number(
            (userWithdrawalRequests[index].result as bigint[])?.[2]
          ),
          unlockedOnBlockNumber: Number(
            ((userWithdrawalRequests[index].result as bigint[])?.[2] || 0n) +
              // (withdrawalDelayBlocks as bigint)
              (call.symbol === ETH
                ? withdrawalDelays.ETH.final
                : withdrawalDelays.LST.final)
          ),
          initialUnlockDay: Number(
            ((userWithdrawalRequests[index].result as bigint[])?.[2] || 0n) +
              // (withdrawalDelayBlocks as bigint)
              (call.symbol === ETH
                ? withdrawalDelays.ETH.initial
                : withdrawalDelays.LST.initial)
          ),
          totalWithdrawDelay: Number(
            ((userWithdrawalRequests[index].result as bigint[])?.[2] || 0n) +
              (withdrawalDelayBlocks as bigint)
          ),
        }))
      : [];

  const firstAvailableWithdrawalNonce: { [x: Hash]: bigint } =
    userWithdrawals.reduce((acc, curr) => {
      const existingEntry = acc[curr.asset];
      if (!existingEntry || curr.nonce < existingEntry) {
        acc = { ...acc, [curr.asset]: curr.nonce };
      }
      return acc;
    }, {} as { [x: Hash]: bigint });

  const refetchWithdrawals = () => {
    refetchAssociatedNonces();
    refetchNextLockedNonces();
  };

  return {
    userWithdrawals,
    refetchWithdrawals,
    firstAvailableWithdrawalNonce,
    isFetching:
      fetchingAssociatedNonces || fetchingRequests || fetchingNextLockedNonces,
  };
};

export default useWithdrawals;
